// Deploy-freshness detection. The build pipeline writes the commit SHA
// into three places: a <meta name="build-version"> tag on index.html
// (the client's baseline for its current session), a /version.txt file
// at the web root (polled to detect new deploys), and the sw.js cache
// name (so old build caches are evicted on activate).

const POLL_INTERVAL_MS = 5 * 60 * 1000;
const INITIAL_POLL_DELAY_MS = 30 * 1000;

export function getBaselineVersion(): string | null {
  if (typeof document === 'undefined') return null;
  const el = document.querySelector<HTMLMetaElement>('meta[name="build-version"]');
  const value = el?.content?.trim() || null;
  // Placeholder leaks in dev / unsubstituted builds — treat as "no baseline".
  if (!value || value === '__BUILD_VERSION__') return null;
  return value;
}

export async function fetchServerVersion(signal?: AbortSignal): Promise<string | null> {
  try {
    const res = await fetch('/version.txt', { cache: 'no-store', signal });
    if (!res.ok) return null;
    const text = (await res.text()).trim();
    return text || null;
  } catch {
    return null;
  }
}

export async function reloadToLatest(): Promise<void> {
  // Tear down any persistent caches before reloading so the browser
  // definitely fetches fresh from origin.
  if ('serviceWorker' in navigator) {
    try {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    } catch {
      /* swallow */
    }
  }
  if (typeof window !== 'undefined' && 'caches' in window) {
    try {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    } catch {
      /* swallow */
    }
  }
  window.location.reload();
}

/**
 * Polls /version.txt periodically and invokes `onUpdate(serverVersion)`
 * when the server advertises a different version than the baseline the
 * current tab was loaded with. Also triggers a check every time the tab
 * regains visibility, so a user coming back after a long idle sees the
 * banner immediately instead of waiting up to POLL_INTERVAL_MS.
 */
export function startVersionPoller(onUpdate: (serverVersion: string) => void): () => void {
  const baseline = getBaselineVersion();
  if (!baseline) return () => {};

  let fired = false;
  const controller = new AbortController();

  const check = async () => {
    if (fired) return;
    const server = await fetchServerVersion(controller.signal);
    if (!server) return;
    if (server !== baseline) {
      fired = true;
      onUpdate(server);
    }
  };

  const initial = setTimeout(check, INITIAL_POLL_DELAY_MS);
  const interval = setInterval(check, POLL_INTERVAL_MS);
  const onVisibility = () => {
    if (document.visibilityState === 'visible') void check();
  };
  document.addEventListener('visibilitychange', onVisibility);

  return () => {
    controller.abort();
    clearTimeout(initial);
    clearInterval(interval);
    document.removeEventListener('visibilitychange', onVisibility);
  };
}
